// new deployment 7/12/2025
import { SignInButton, SignUpButton, useClerk, useUser } from "@clerk/clerk-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";
import UserMentionDropdown from "../../components/UserMentionDropdown/UserMentionDropdown";
import useNotifications from "../../hooks/useNotifications";
import {
    getMentionedUsernames,
    getMentionSearchTerm
} from "../../utils/mentionUtils";

import {
    MagnifyingGlassIcon,
    MapPinIcon,
    PlusIcon,
    XMarkIcon
} from "@heroicons/react/24/solid";
import NotificationModal from "../../components/NotificationModal/NotificationModal";
import RecommendationCard from "../../components/RecommendationCard/RecommendationCard";
import ReviewModal from "../../components/ReviewModal/ReviewModal";
import SuccessModal from "../../components/SuccessModal/SuccessModal";
import TrustScoreWheel from '../../components/TrustScoreWheel/TrustScoreWheel';
import { API_URL } from "../../utils/constants";
import "./Home.css";

const stateMap = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

const stateNameMap = Object.fromEntries(
  Object.entries(stateMap).map(([abbr, name]) => [name.toLowerCase(), name])
);

const getFullStateName = (input) => {
  if (!input) return "";
  const trimmed = input.trim();
  const upper = trimmed.toUpperCase();

  if (stateMap[upper]) {
    return stateMap[upper];
  }

  const lower = trimmed.toLowerCase();
  if (stateNameMap[lower]) {
    return stateNameMap[lower];
  }
  return trimmed;
};

const BRAND_PHRASE = "Hyve";

// Activity feed templates for dynamic content
const ACTIVITY_TEMPLATES = [
    // Trust points activities
    { avatar: "B", text: "Benny earned <strong>50 trust points</strong>", time: "2 min ago", hasPulse: true },
    { avatar: "K", text: "Kyle earned <strong>75 trust points</strong>", time: "3 min ago", hasPulse: true },
    { avatar: "A", text: "Alex earned <strong>100 trust points</strong>", time: "4 min ago", hasPulse: true },
    { avatar: "V", text: "Venkat earned <strong>25 trust points</strong>", time: "6 min ago", hasPulse: true },
    { avatar: "N", text: "Nikhil earned <strong>150 trust points</strong>", time: "8 min ago", hasPulse: true },
    { avatar: "E", text: "Emma earned <strong>80 trust points</strong>", time: "10 min ago", hasPulse: true },
    { avatar: "A", text: "Amit earned <strong>120 trust points</strong>", time: "12 min ago", hasPulse: true },
    { avatar: "B", text: "Brady earned <strong>90 trust points</strong>", time: "15 min ago", hasPulse: true },
    
    // Community joins
    { avatar: "D", text: "Dhruv joined <strong>Seattle Foodies</strong>", time: "5 min ago", hasPulse: false },
    { avatar: "S", text: "Sarah joined <strong>St. James Cathedral - Seattle</strong>", time: "7 min ago", hasPulse: false },
    { avatar: "A", text: "Alex joined <strong>New Moms in Seattle</strong>", time: "9 min ago", hasPulse: false },
    { avatar: "J", text: "Jessica joined <strong>Restaurant Lovers</strong>", time: "11 min ago", hasPulse: false },
    { avatar: "D", text: "David joined <strong>Auto Experts</strong>", time: "13 min ago", hasPulse: false },
    { avatar: "M", text: "Michael joined <strong>Cleaners in Seattle</strong>", time: "16 min ago", hasPulse: false },
    { avatar: "R", text: "Ryan joined <strong>Moving Services</strong>", time: "18 min ago", hasPulse: false },
    { avatar: "L", text: "Lisa joined <strong>Outdoor Experts</strong>", time: "20 min ago", hasPulse: false },
    
    // Recommendations shared
    { avatar: "A", text: "Alex shared a <strong>home service rec from Yelp</strong>", time: "12 min ago", hasPulse: false },
    { avatar: "S", text: "Sarah shared a <strong>restaurant rec from Beli</strong>", time: "14 min ago", hasPulse: false },
    { avatar: "M", text: "Mike shared a <strong>CPA rec from Angi's List</strong>", time: "17 min ago", hasPulse: false },
    { avatar: "J", text: "Jessica shared a <strong>cleaning service rec</strong>", time: "19 min ago", hasPulse: false },
    { avatar: "D", text: "David shared a <strong>auto repair rec</strong>", time: "22 min ago", hasPulse: false },
    { avatar: "E", text: "Emma shared a <strong>moving company rec</strong>", time: "25 min ago", hasPulse: false },
    { avatar: "R", text: "Ryan shared a <strong>landscaping rec</strong>", time: "28 min ago", hasPulse: false },
    { avatar: "L", text: "Lisa shared a <strong>plumbing rec</strong>", time: "30 min ago", hasPulse: false },
    
    // Level achievements
    { avatar: "J", text: "Jessica reached <strong>Level 3</strong>", time: "18 min ago", hasPulse: true },
    { avatar: "M", text: "Mike reached <strong>Level 21</strong>", time: "21 min ago", hasPulse: true },
    { avatar: "S", text: "Sarah reached <strong>Level 4</strong>", time: "24 min ago", hasPulse: true },
    { avatar: "A", text: "Alex reached <strong>Level 15</strong>", time: "27 min ago", hasPulse: true },
    { avatar: "K", text: "Kenneth reached <strong>Level 1</strong>", time: "29 min ago", hasPulse: true },
    { avatar: "E", text: "Emma reached <strong>Level 3</strong>", time: "32 min ago", hasPulse: true },
    { avatar: "R", text: "Ryan reached <strong>Level 12</strong>", time: "35 min ago", hasPulse: true },
    { avatar: "M", text: "Michelle reached <strong>Level 4</strong>", time: "38 min ago", hasPulse: true },
    
    // Reviews and ratings
    { avatar: "S", text: "Sarah left a <strong>5-star recommendation</strong>", time: "4 min ago", hasPulse: false },
    { avatar: "K", text: "Kyle left a <strong>4-star recommendation</strong>", time: "6 min ago", hasPulse: false },
    { avatar: "A", text: "Alex left a <strong>5-star recommendation</strong>", time: "8 min ago", hasPulse: false },
    { avatar: "B", text: "Brady left a <strong>3-star recommendation</strong>", time: "10 min ago", hasPulse: false },
    { avatar: "A", text: "Amit left a <strong>5-star recommendation</strong>", time: "12 min ago", hasPulse: false },
    { avatar: "E", text: "Emma left a <strong>4-star recommendation</strong>", time: "15 min ago", hasPulse: false },
    { avatar: "R", text: "Ryan left a <strong>5-star recommendation</strong>", time: "18 min ago", hasPulse: false },
    { avatar: "L", text: "Lisa left a <strong>4-star recommendation</strong>", time: "20 min ago", hasPulse: false },
    
    // Connections and follows
    { avatar: "M", text: "Mike gained <strong>3 new followers</strong>", time: "5 min ago", hasPulse: true },
    { avatar: "B", text: "Brady gained <strong>2 new followers</strong>", time: "7 min ago", hasPulse: true },
    { avatar: "A", text: "Alex gained <strong>5 new followers</strong>", time: "9 min ago", hasPulse: true },
    { avatar: "J", text: "Jessica gained <strong>1 new follower</strong>", time: "11 min ago", hasPulse: true },
    { avatar: "J", text: "Jonny gained <strong>4 new followers</strong>", time: "13 min ago", hasPulse: true },
    { avatar: "A", text: "Amit gained <strong>2 new followers</strong>", time: "16 min ago", hasPulse: true },
    { avatar: "R", text: "Ryan gained <strong>3 new followers</strong>", time: "19 min ago", hasPulse: true },
    { avatar: "M", text: "Michael gained <strong>1 new follower</strong>", time: "22 min ago", hasPulse: true },
    
    // Special achievements
    { avatar: "S", text: "Sarah became a <strong>Trusted Advisor</strong>", time: "3 min ago", hasPulse: true },
    { avatar: "A", text: "Amit earned <strong>Community Leader</strong> badge", time: "6 min ago", hasPulse: true },
    { avatar: "A", text: "Alex earned <strong>Top Recommender</strong> badge", time: "9 min ago", hasPulse: true },
    { avatar: "J", text: "Jessica earned <strong>Most Trusted in Seattle</strong> badge", time: "12 min ago", hasPulse: true },
    { avatar: "D", text: "David earned <strong>Auto Expert</strong> badge", time: "15 min ago", hasPulse: true },
    { avatar: "E", text: "Emma earned <strong>Platinum Contributor</strong> badge", time: "18 min ago", hasPulse: true },
    { avatar: "K", text: "Kyle earned <strong>Community Champion</strong> badge", time: "21 min ago", hasPulse: true },
    { avatar: "L", text: "Lisa earned <strong>Movie Connoisseur</strong> badge", time: "24 min ago", hasPulse: true }
];

// Function to get random activities
const getRandomActivities = (count = 4) => {
    const shuffled = [...ACTIVITY_TEMPLATES].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    
    // Sort by time (most recent first)
    return selected.sort((a, b) => {
        const timeA = parseInt(a.time.split(' ')[0]);
        const timeB = parseInt(b.time.split(' ')[0]);
        return timeA - timeB; // Ascending order (2 min ago comes before 18 min ago)
    });
};

const Home = () => {
    const { user, isLoaded, isSignedIn } = useUser();
    const { openSignIn } = useClerk();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery({ maxWidth: 768 });

    // Smart notification management
    const {
        unreadCount: newNotificationsCount,
        notifications,
        isLoadingCount: isLoadingNotificationsCount,
        isLoadingNotifications,
        fetchNotifications,
        markAsRead: handleMarkNotificationAsRead,
        markAllAsRead: handleMarkAllNotificationsAsRead,
        forceRefresh: refreshNotifications
    } = useNotifications();

    // Essential UI state variables
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showMobileAuthModal, setShowMobileAuthModal] = useState(false);
    const [showMainFilterDropdown, setShowMainFilterDropdown] = useState(false);
    const [preferredName, setPreferredName] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [locationInput, setLocationInput] = useState('');
    const [isEditingLocation, setIsEditingLocation] = useState(false);

    // Typing animation state
    const [displayText, setDisplayText] = useState("");
    const [isTyping, setIsTyping] = useState(true);

    // This state will hold the full user profile fetched from our backend
    const [dbUser, setDbUser] = useState(null);

    // Other state variables
    const [newRecsCount, setNewRecsCount] = useState(0);
    const [isLoadingNewRecsCount, setIsLoadingNewRecsCount] = useState(true);
    
    const [providerCount, setProviderCount] = useState(0);
    const [connectionCount, setConnectionCount] = useState(0);
    const [communityCount, setCommunityCount] = useState(0);
    const [isLoadingCounts, setIsLoadingCounts] = useState(true);
    
    const [recentRecommendations, setRecentRecommendations] = useState([]);
    const [isLoadingRecentRecommendations, setIsLoadingRecentRecommendations] = useState(true);
    const [likedRecommendations, setLikedRecommendations] = useState(new Set());
    const [recentRecommendationsError, setRecentRecommendationsError] = useState(null);

    const [publicRecommendations, setPublicRecommendations] = useState([]);
    const [isLoadingPublicRecommendations, setIsLoadingPublicRecommendations] = useState(true);
    const [publicRecommendationsError, setPublicRecommendationsError] = useState(null);

    // Batch comments state
    const [recentCommentsMap, setRecentCommentsMap] = useState(new Map());
    const [publicCommentsMap, setPublicCommentsMap] = useState(new Map());
    const [isLoadingComments, setIsLoadingComments] = useState(false);

    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [providerForReview, setProviderForReview] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // Notification modal state
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

    // Loading states for counts
    const [showStatsLine, setShowStatsLine] = useState(false);

    // Mock data for new dashboard features
    const [progressToNextLevel, setProgressToNextLevel] = useState(0);
    const [currentLevel, setCurrentLevel] = useState(0);
    const [userScore, setUserScore] = useState(0);
    const [isLoadingUserScore, setIsLoadingUserScore] = useState(true);

    // Dynamic activity feed state
    const [randomActivities, setRandomActivities] = useState([]);

    // Trust circles state
    const [trustCircles, setTrustCircles] = useState([]);
    const [isLoadingTrustCircles, setIsLoadingTrustCircles] = useState(true);
    
    // Filter state
    const [currentFilterMode, setCurrentFilterMode] = useState('all'); // 'all', 'myTrust', 'specific'
    const [selectedTrustCircles, setSelectedTrustCircles] = useState([]);
    const [showTrustCircleDropdown, setShowTrustCircleDropdown] = useState(false);

    // Leaderboard data from connections
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);

    // User mention functionality state
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionSearchTerm, setMentionSearchTerm] = useState("");
    const [mentionDropdownPosition, setMentionDropdownPosition] = useState({ top: 0, left: 0 });
    const [searchInputRef, setSearchInputRef] = useState(null);
    const searchInputElementRef = useRef(null);
    
    // Hover tooltip state for existing mentions
    const [hoveredMention, setHoveredMention] = useState(null);
    const [mentionHoverPosition, setMentionHoverPosition] = useState({ top: 0, left: 0 });
    
    // Get user location for spatial search
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        // Update local storage whenever location changes, ensuring persistence for non-logged-in users
        // or as a fallback.
        if (city && state) {
            localStorage.setItem('userSelectedLocation', `${city}, ${state}`);
        }
    }, [city, state]);

    // Get user location for spatial search
    useEffect(() => {
        if (!userLocation && navigator.geolocation) {
            console.log("[HOME] Requesting user location for spatial search...");
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(newLocation);
                    console.log(`[HOME] Location obtained: ${newLocation.lat}, ${newLocation.lng}`);
                },
                (error) => {
                    console.log("Geolocation error:", error);
                    // Fallback to a default location (USA center)
                    const fallbackLocation = {
                        lat: 39.8283,
                        lng: -98.5795,
                    };
                    setUserLocation(fallbackLocation);
                    console.log(`[HOME] Using fallback location: ${fallbackLocation.lat}, ${fallbackLocation.lng}`);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000, // 5 minutes
                }
            );
        } else if (!userLocation && !navigator.geolocation) {
            // Fallback for browsers that don't support geolocation
            const fallbackLocation = {
                lat: 39.8283,
                lng: -98.5795,
            };
            setUserLocation(fallbackLocation);
            console.log(`[HOME] Browser doesn't support geolocation, using fallback: ${fallbackLocation.lat}, ${fallbackLocation.lng}`);
        }
    }, [userLocation]);

    const handleUpdateLocation = async () => {
        setIsEditingLocation(false);
        const originalLocation = `${city}, ${state}`;

        if (locationInput !== originalLocation && isSignedIn && user) {
            const parts = locationInput.split(',').map(p => p.trim());
            const newCity = parts[0] || '';
            const newState = parts.length > 1 ? getFullStateName(parts[1]) : '';

            if (!newCity || !newState) {
                console.warn("Invalid location format. Reverting.");
                setLocationInput(`${city}, ${state}`); // Revert input field
                return;
            }

            const previousCity = city;
            const previousState = state;

            // Optimistically update UI
            setCity(newCity);
            setState(newState);
            setLocationInput(`${newCity}, ${newState}`); // Update input to reflect change

            try {
                const session = await window.Clerk.session.getToken();
                const response = await fetch(`${API_URL}/api/users/me/location`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session}`
                    },
                    body: JSON.stringify({
                        email: user.primaryEmailAddress.emailAddress,
                        location: newCity,
                        state: newState,
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to update location on the backend.");
                }
            } catch (error) {
                console.error("Error updating location, reverting UI change.", error);
                // Revert the optimistic update
                setCity(previousCity);
                setState(previousState);
                setLocationInput(`${previousCity}, ${previousState}`);
            }
        }
    };

    // Hyve gradient color function (light yellow to brand yellow)
    const getHyveGradientColor = (percentage) => {
        // Light yellow: #fff8e1 (255, 248, 225)
        // Brand yellow: #ffb72e (255, 183, 46)
        const lightYellow = { r: 255, g: 248, b: 225 };
        const brandYellow = { r: 255, g: 183, b: 46 };
        
        const ratio = percentage / 100;
        const r = Math.round(lightYellow.r + (brandYellow.r - lightYellow.r) * ratio);
        const g = Math.round(lightYellow.g + (brandYellow.g - lightYellow.g) * ratio);
        const b = Math.round(lightYellow.b + (brandYellow.b - lightYellow.b) * ratio);
        
        return `rgb(${r}, ${g}, ${b})`;
    };

    const particlesInit = useCallback(async (engine) => {
        await loadFull(engine);
    }, []);

    const particlesOptions = useMemo(() => ({
        background: { color: { value: "transparent" } },
        fpsLimit: 60,
        interactivity: {
            events: {
                onHover: { enable: true, mode: "grab" },
                onClick: { enable: false, mode: "push" },
            },
            modes: {
                grab: { distance: 140, links: { opacity: 0.1 } },
                push: { quantity: 1 },
            },
        },
        particles: {
            color: { value: "#ffb72e" },
            links: { color: "#ffb72e", distance: 150, enable: true, opacity: 0.25, width: 1, triangles: { enable: true, opacity: 0.02, color: { value: "#ffb72e" } } },
            collisions: { enable: false },
            move: { direction: "none", enable: true, outModes: { default: "bounce" }, random: true, speed: 0.5, straight: false },
            number: { density: { enable: true, area: 900 }, value: isMobile ? 30 : 60 },
            opacity: { value: { min: 0.1, max: 0.35 }, animation: { enable: true, speed: 0.6, minimumValue: 0.05, sync: false } },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 2.5 }, animation: { enable: true, speed: 1.5, minimumValue: 0.5, sync: false } },
        },
        detectRetina: true,
        fullScreen: { enable: true, zIndex: -1 }
    }), [isMobile]);

    const handleOpenReviewModal = (provider) => {
        if (!isSignedIn) { openSignIn(); return; }
        
        // Create URL parameters for prefilling the recommendation form
        const params = new URLSearchParams();
        
        // Add the provider ID to link to existing service provider
        if (provider.id) {
            params.set('providerId', provider.id);
        }
        
        if (provider.business_name || provider.name) {
            params.set('businessName', provider.business_name || provider.name);
        }
        if (provider.contact_name) {
            params.set('contactName', provider.contact_name);
        }
        if (provider.website) {
            params.set('website', provider.website);
        }
        if (provider.phone_number) {
            params.set('phoneNumber', provider.phone_number);
        }
        if (provider.street_address) {
            params.set('streetAddress', provider.street_address);
        }
        if (provider.city) {
            params.set('city', provider.city);
        }
        if (provider.state) {
            params.set('state', provider.state);
        }
        if (provider.zip_code) {
            params.set('zipCode', provider.zip_code);
        }
        if (provider.google_place_id) {
            params.set('googlePlaceId', provider.google_place_id);
        }
        if (provider.latitude && provider.longitude) {
            params.set('latitude', provider.latitude);
            params.set('longitude', provider.longitude);
        }
        if (provider.tags && Array.isArray(provider.tags) && provider.tags.length > 0) {
            params.set('tags', provider.tags.join(', '));
        }
        
        // Navigate to share-recommendation page with prefilled data
        navigate(`/share-recommendation?${params.toString()}`);
    };
    const handleCloseReviewModal = () => setIsReviewModalOpen(false);

    const updateRecommendation = async (providerId, reviewData) => {
        try {
            // Fetch the updated provider data that includes the new review stats
            const params = new URLSearchParams({
                user_id: user?.id,
                email: user?.primaryEmailAddress?.emailAddress,
            });
            const response = await fetch(`${API_URL}/api/providers/${providerId}?${params.toString()}`);
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.provider) {
                    const updatedProvider = data.provider;
                    setRecentRecommendations(prevRecs => {
                        return prevRecs.map(rec => {
                            if ((rec.provider_id || rec.id) === providerId) {
                                const existingTags = Array.isArray(rec.tags) ? rec.tags : [];
                                const newTags = Array.isArray(reviewData.tags) ? reviewData.tags : [];
                                const allTags = [...new Set([...existingTags, ...newTags])];
        
                                return {
                                    ...rec,
                                    average_rating: parseFloat(updatedProvider.average_rating) || rec.average_rating,
                                    total_reviews: parseInt(updatedProvider.total_reviews, 10) || rec.total_reviews,
                                    users_who_reviewed: updatedProvider.users_who_reviewed || rec.users_who_reviewed || [],
                                    tags: allTags
                                };
                            }
                            return rec;
                        });
                    });
                }
            }
        } catch (error) {
            console.error(`Failed to update recommendation ${providerId}:`, error);
            fetchRecentRecommendations();
        }
    };

    const handleSubmitReview = async (reviewData) => {
        if (!providerForReview || !user || !dbUser) return;
        const session = window.Clerk.session;
        if (!session) {
            console.error("Clerk session not available");
            return;
        }
        const token = await session.getToken();
        if (!token) {
            console.error("Not authenticated, no token");
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/reviews`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    provider_id:
                        providerForReview.provider_id || providerForReview.id,
                    provider_email: providerForReview.email || "",
                    user_id: user?.id,
                    email: user.primaryEmailAddress?.emailAddress,
                    rating: reviewData.rating,
                    content: reviewData.review,
                    tags: reviewData.tags,
                }),
            });
            if (!response.ok) throw new Error("Failed to submit review");

            handleCloseReviewModal();
            setSuccessMessage(
                "Your review has been submitted successfully. Thank you!"
            );
            setShowSuccessModal(true);
            const providerId = providerForReview.provider_id || providerForReview.id;
            updateRecommendation(providerId, reviewData);
        } catch (error) {
            console.error("Error submitting review:", error);
        }
    };

    const handleLikeRecommendation = async (providerId) => {
        if (!isSignedIn || !user || !dbUser) {
            openSignIn();
            return;
        }

                        const currentUserId = user?.id;
        const recommendation = recentRecommendations.find(rec => (rec.provider_id || rec.id) === providerId);
        if (!recommendation) {
            console.error("Recommendation not found for like action:", providerId);
            return;
        }

        const originallyLiked = likedRecommendations.has(providerId);
        const originalNumLikes = recommendation.num_likes || 0;
        const newNumLikesOptimistic = originallyLiked ? originalNumLikes - 1 : originalNumLikes + 1;

        setRecentRecommendations(prevRecs =>
            prevRecs.map(rec =>
                (rec.provider_id || rec.id) === providerId
                    ? { ...rec, num_likes: newNumLikesOptimistic, currentUserLiked: !originallyLiked }
                    : rec
            )
        );
        setLikedRecommendations(prevLiked => {
            const newSet = new Set(prevLiked);
            if (originallyLiked) {
                newSet.delete(providerId);
            } else {
                newSet.add(providerId);
            }
            return newSet;
        });

        try {
            const session = window.Clerk.session;
            if (!session) { throw new Error("Clerk session not available for like action."); }
            const token = await session.getToken();
            if (!token) { throw new Error("Not authenticated (no token) for like action."); }

            const response = await fetch(`${API_URL}/api/providers/${providerId}/like`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId: currentUserId,
                    userEmail: user.primaryEmailAddress.emailAddress
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: "Server error during like action." }));
                throw new Error(errorData.message || `Failed to update like status. Status: ${response.status}`);
            }

            const result = await response.json();

            setRecentRecommendations(prevRecs =>
                prevRecs.map(rec =>
                    (rec.provider_id || rec.id) === providerId
                        ? { ...rec, num_likes: parseInt(result.num_likes, 10) || 0, currentUserLiked: result.currentUserLiked }
                        : rec
                )
            );
            setLikedRecommendations(prevLiked => {
                const newSet = new Set(prevLiked);
                if (result.currentUserLiked) {
                    newSet.add(providerId);
                } else {
                    newSet.delete(providerId);
                }
                return newSet;
            });

        } catch (error) {
            console.error("Error updating like status:", error.message);
            setRecentRecommendations(prevRecs =>
                prevRecs.map(rec =>
                    (rec.provider_id || rec.id) === providerId
                        ? { ...rec, num_likes: originalNumLikes, currentUserLiked: originallyLiked }
                        : rec
                )
            );
            setLikedRecommendations(prevLiked => {
                const newSet = new Set(prevLiked);
                if (originallyLiked) {
                    newSet.add(providerId);
                } else {
                    newSet.delete(providerId);
                }
                return newSet;
            });
        }
    };

    // Generate random activities on component mount
    useEffect(() => {
        setRandomActivities(getRandomActivities(4));
    }, []);

    // Expose hover functions to window for inline event handlers
    useEffect(() => {
        window.handleMentionHover = handleMentionHover;
        window.handleMentionLeave = handleMentionLeave;
        
        return () => {
            delete window.handleMentionHover;
            delete window.handleMentionLeave;
        };
    }, []);

    // Add click outside listener for dropdowns
    useEffect(() => {
        if (showTrustCircleDropdown || showMainFilterDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [showTrustCircleDropdown, showMainFilterDropdown]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!isLoaded || !isSignedIn || !user?.primaryEmailAddress?.emailAddress) {
                setPreferredName("");
                setUserScore(0);
                setCurrentLevel(0);
                setProgressToNextLevel(0);
                setIsLoadingUserScore(false);
                setDbUser(null);
                return;
            }
            
            setIsLoadingUserScore(true);
            try {
                const response = await fetch(`${API_URL}/api/users/preferred-name?email=${encodeURIComponent(user.primaryEmailAddress.emailAddress)}`);
                if (response.ok) {
                    const data = await response.json();
                    //console.log('Raw API Response:', data);
                    
                    setDbUser(data); // Store the full user object from the backend.

                    setPreferredName(data.preferredName || "");
                    
                    if (data.location && data.state) {
                        setCity(data.location);
                        setState(data.state);
                    } else {
                        // If no location in DB, fall back to localStorage or a default
                        const cachedLocation = localStorage.getItem('userSelectedLocation') || 'Seattle, WA';
                        const parts = cachedLocation.split(',').map(p => p.trim());
                        setCity(parts[0] || 'Seattle');
                        setState(parts[1] || 'WA');
                    }
                    
                    const score = parseInt(data.userScore || data.user_score) || 0;
                    //console.log('Parsed score:', score, 'Original userScore:', data.userScore, 'Original user_score:', data.user_score, 'Type:', typeof data.userScore);
                    
                    const { level, progress } = calculateLevel(score);
                    //console.log('Calculated values:', { score, level, progress });
                    
                    setUserScore(score);
                    setCurrentLevel(level);
                    setProgressToNextLevel(progress);
                } else {
                    setPreferredName("");
                    setUserScore(0);
                    setCurrentLevel(0);
                    setProgressToNextLevel(0);
                    setDbUser(null);
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setPreferredName("");
                setUserScore(0);
                setCurrentLevel(0);
                setProgressToNextLevel(0);
                setDbUser(null);
            } finally {
                setIsLoadingUserScore(false);
            }
        };
        fetchUserData();
    }, [isLoaded, isSignedIn, user]);

    // Fetch new recommendations count
    useEffect(() => {
        const fetchNewRecsCount = async () => {
            if (!isLoaded || !isSignedIn || !user?.primaryEmailAddress?.emailAddress || !dbUser) {
                setNewRecsCount(0);
                setIsLoadingNewRecsCount(false);
                return;
            }

            setIsLoadingNewRecsCount(true);
            try {
                const params = new URLSearchParams({
                    user_id: user?.id,
                    email: user.primaryEmailAddress.emailAddress,
                });
                
                const response = await fetch(`${API_URL}/api/providers/new/count?${params.toString()}`);
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setNewRecsCount(data.newRecommendationCount || 0);
                    } else {
                        console.error("API returned unsuccessful response:", data.message);
                        setNewRecsCount(0);
                    }
                } else {
                    console.error("Failed to fetch new recommendations count:", response.statusText);
                    setNewRecsCount(0);
                }
            } catch (error) {
                console.error("Error fetching new recommendations count:", error);
                setNewRecsCount(0);
            } finally {
                setIsLoadingNewRecsCount(false);
            }
        };

        fetchNewRecsCount();
    }, [isLoaded, isSignedIn, user, dbUser]);

    useEffect(() => {
        if (!isLoaded) return;
        const fetchCounts = async () => {
            if (!isSignedIn || !user || !dbUser) {
                setProviderCount(0);
                setConnectionCount(0);
                setCommunityCount(0);
                setTrustCircles([]);
                setIsLoadingCounts(false);
                setIsLoadingTrustCircles(false);
                return;
            }
            
            setIsLoadingCounts(true);
            setIsLoadingTrustCircles(true);
            try {
                const params = new URLSearchParams({
                    user_id: user?.id,
                    email: user.primaryEmailAddress?.emailAddress,
                    firstName: user.firstName || "",
                    lastName: user.lastName || ""
                });
                const providerRes = await fetch(`${API_URL}/api/providers/count?${params.toString()}`);
                if (providerRes.ok) {
                    const d = await providerRes.json();
                    setProviderCount(d.count || 0);
                } else {
                    setProviderCount(0);
                }

                const connRes = await fetch(`${API_URL}/api/connections/followers?user_id=${user?.id}`);
                if (connRes.ok) {
                    const d = await connRes.json();
                    setConnectionCount(Array.isArray(d) ? d.length : 0);
                } else {
                    setConnectionCount(0);
                }

                const communityRes = await fetch(`${API_URL}/api/communities/count/communities`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: dbUser.clerkId,
                        email: user.primaryEmailAddress.emailAddress
                    }),
                });
                if (communityRes.ok) {
                    const data = await communityRes.json();
                    setCommunityCount(data.count || 0);
                    // Also set the trust circles data from the same response
                    if (data.trustCircles) {
                        setTrustCircles(data.trustCircles);
                        setIsLoadingTrustCircles(false);
                    }
                } else {
                    setCommunityCount(0);
                    setTrustCircles([]);
                    setIsLoadingTrustCircles(false);
                    console.error("Failed to fetch community count:", communityRes.statusText);
                }

            } catch (err) {
                console.error("Error fetching counts:", err);
                setProviderCount(0);
                setConnectionCount(0);
                setCommunityCount(0);
                setTrustCircles([]);
                setIsLoadingTrustCircles(false);
            } finally {
                setIsLoadingCounts(false);
            }
        };
        fetchCounts();
    }, [isLoaded, isSignedIn, user, dbUser]);

    const fetchRecentRecommendations = useCallback(async () => {
        if (!isLoaded) return;
        setIsLoadingRecentRecommendations(true);
        setRecentRecommendationsError(null);
        const newLikedSet = new Set();

        try {
            let rawData;
            if (isSignedIn && user && dbUser) {
                const params = new URLSearchParams({
                    user_id: dbUser.clerkId,
                    email: user.primaryEmailAddress?.emailAddress,
                    firstName: user.firstName || "",
                    lastName: user.lastName || "",
                    limit: '3',
                    sortBy: 'date_of_recommendation',
                    sortOrder: 'desc'
                });
                const response = await fetch(`${API_URL}/api/providers/newest-visible?${params.toString()}`);
                if (!response.ok) throw new Error(`API error: ${response.statusText}`);
                const jsonResponse = await response.json();
                rawData = jsonResponse.success && jsonResponse.providers ? jsonResponse.providers : [];
            } else {
                rawData = [];
            }

            if (rawData.length === 0) {
                setRecentRecommendations([]);
                setIsLoadingRecentRecommendations(false);
                return;
            }

            // Data now comes directly from the backend with reviews and stats included
            const processedData = rawData.map(p => {
                const providerId = p.provider_id || p.id;
                if (p.currentUserLiked) {
                    newLikedSet.add(providerId);
                }

                return {
                    ...p,
                    id: providerId,
                    average_rating: parseFloat(p.average_rating) || 0,
                    total_reviews: parseInt(p.total_reviews, 10) || 0,
                    users_who_reviewed: p.users_who_reviewed || [],
                };
            });

            setRecentRecommendations(processedData);
            setLikedRecommendations(newLikedSet);

        } catch (error) {
            console.error("Error fetching recent recommendations:", error);
            setRecentRecommendationsError(error.message);
            setRecentRecommendations([]);
            setLikedRecommendations(new Set());
        } finally {
            setIsLoadingRecentRecommendations(false);
        }
    }, [isLoaded, isSignedIn, user, dbUser, API_URL]);

    useEffect(() => {
        fetchRecentRecommendations();
    }, [fetchRecentRecommendations]);

    // Fetch leaderboard data from connections
    useEffect(() => {
        const fetchLeaderboardData = async () => {
            if (!isLoaded || !isSignedIn || !user?.primaryEmailAddress?.emailAddress || isLoadingUserScore || !dbUser) {
                setLeaderboardData([]);
                setIsLoadingLeaderboard(false);
                return;
            }

            setIsLoadingLeaderboard(true);
            try {
                // Fetch connections with scores
                const response = await fetch(`${API_URL}/api/connections/check-connections`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: user.primaryEmailAddress.emailAddress }),
                });

                let connections = [];
                if (response.ok) {
                    connections = await response.json();
                }

                // Create leaderboard array with current user and connections
                const allUsers = [
                    {
                        name: preferredName || user.firstName || "You",
                        username: user.username || null, // Current user's username
                        user_score: userScore,
                        isCurrentUser: true,
                        avatar: preferredName ? preferredName.charAt(0).toUpperCase() : user.firstName ? user.firstName.charAt(0).toUpperCase() : "U"
                    },
                    ...connections.map(conn => ({
                        name: conn.name || "Unknown",
                        username: conn.username || null,
                        user_score: conn.user_score || 0,
                        isCurrentUser: false,
                        avatar: conn.name ? conn.name.charAt(0).toUpperCase() : "U"
                    }))
                ];

                // Sort by score and add ranks
                const sortedUsers = allUsers
                    .sort((a, b) => (b.user_score || 0) - (a.user_score || 0))
                    .map((user, index) => ({
                        ...user,
                        rank: index + 1,
                        score: user.user_score || 0
                    }));

                // Find current user's position
                const currentUserIndex = sortedUsers.findIndex(user => user.isCurrentUser);
                const currentUserRank = currentUserIndex + 1;

                let displayUsers = [];
                
                if (currentUserRank <= 5) {
                    // Current user is in top 5, show top 5
                    displayUsers = sortedUsers.slice(0, 5);
                } else {
                    // Current user is not in top 5, show top 4 + separator + current user
                    const top4 = sortedUsers.slice(0, 4);
                    const currentUser = sortedUsers[currentUserIndex];
                    
                    displayUsers = [
                        ...top4,
                        { isSeparator: true }, // Special separator item
                        currentUser
                    ];
                }

                setLeaderboardData(displayUsers);
            } catch (error) {
                console.error("Error fetching leaderboard data:", error);
                // Fallback to just current user
                setLeaderboardData([{
                    rank: 1,
                    name: preferredName || user.firstName || "You",
                    score: userScore,
                    avatar: preferredName ? preferredName.charAt(0).toUpperCase() : user.firstName ? user.firstName.charAt(0).toUpperCase() : "U",
                    isCurrentUser: true
                }]);
            } finally {
                setIsLoadingLeaderboard(false);
            }
        };

        fetchLeaderboardData();
    }, [isLoaded, isSignedIn, user, userScore, preferredName, isLoadingUserScore, dbUser]);

    // Fetch public recommendations for non-logged-in users
    useEffect(() => {
        const fetchPublicRecommendations = async () => {
            if (isSignedIn) {
                setPublicRecommendations([]);
                setIsLoadingPublicRecommendations(false);
                return;
            }

            setIsLoadingPublicRecommendations(true);
            setPublicRecommendationsError(null);

            try {
                const response = await fetch(`${API_URL}/api/providers/public?limit=3&sortBy=date_of_recommendation&sortOrder=desc`);
                if (!response.ok) throw new Error(`API error: ${response.statusText}`);
                
                const jsonResponse = await response.json();
                const rawData = jsonResponse.success && jsonResponse.providers ? jsonResponse.providers : [];

                if (rawData.length === 0) {
                    setPublicRecommendations([]);
                    setIsLoadingPublicRecommendations(false);
                    return;
                }

                // Data now comes directly from the backend with reviews and stats included
                const processedData = rawData.map(p => ({
                    ...p,
                    provider_id: p.id,
                    average_rating: parseFloat(p.average_rating) || 0,
                    total_reviews: parseInt(p.total_reviews, 10) || 0,
                    users_who_reviewed: p.users_who_reviewed || [],
                }));

                setPublicRecommendations(processedData);
            } catch (error) {
                console.error("Error fetching public recommendations:", error);
                setPublicRecommendationsError(error.message);
                setPublicRecommendations([]);
            } finally {
                setIsLoadingPublicRecommendations(false);
            }
        };

        fetchPublicRecommendations();
    }, [isSignedIn, API_URL]);

    // Fetch comments for recent recommendations
    useEffect(() => {
        if (!isLoadingRecentRecommendations && recentRecommendations.length > 0) {
            fetchBatchComments(recentRecommendations, setRecentCommentsMap);
        }
    }, [recentRecommendations, isLoadingRecentRecommendations]);

    // Fetch comments for public recommendations
    useEffect(() => {
        if (!isLoadingPublicRecommendations && publicRecommendations.length > 0) {
            fetchBatchComments(publicRecommendations, setPublicCommentsMap);
        }
    }, [publicRecommendations, isLoadingPublicRecommendations]);

    const handleSearch = async (e, queryOverride = null) => {
        if (e) e.preventDefault();
        const q = (queryOverride || searchQuery).trim();
        console.log('handleSearch called:', { q, isLoaded, isSignedIn, dbUser, isMobile, queryOverride });
        if (!q || !isLoaded) return;
        if (!isSignedIn || !dbUser) {
            if (isMobile) {
                setShowMobileAuthModal(true);
            } else {
                openSignIn();
            }
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        try {
            const finalState = getFullStateName(state);
            
            // Extract mentioned usernames from the query
            const mentionedUsernames = getMentionedUsernames(q);
            
            // Get user location for spatial search
            let locationParams = {};
            if (userLocation) {
                locationParams = {
                    latitude: userLocation.lat,
                    longitude: userLocation.lng,
                    max_distance: 50000
                };
                console.log(`[HOME] Including location in search: ${userLocation.lat}, ${userLocation.lng}`);
            } else {
                console.log(`[HOME] No location available for search`);
            }
            
            const params = new URLSearchParams({
                q: q,
                user_id: dbUser.clerkId,
                email: user.primaryEmailAddress?.emailAddress,
                state: finalState,
                ...locationParams
            });
            
            // Add mentioned users as a parameter if any are found
            if (mentionedUsernames.length > 0) {
                params.set('mentioned_users', mentionedUsernames.join(','));
            }
            
            // Add search context based on current filter mode
            let searchContext = 'general';
            let contextParams = {};
            
            // On mobile, always use "All Recs" mode regardless of current filter
            if (isMobile) {
                searchContext = 'general';
            } else {
                // Temporarily make "My Trust Circle" and "Specific Trust Circles" behave like "All Recs" until backend is fixed
                if (currentFilterMode === 'myTrust') {
                    searchContext = 'general'; // Changed from 'trust-circle' to 'general'
                } else if (currentFilterMode === 'specific' && selectedTrustCircles.length > 0) {
                    searchContext = 'general'; // Changed from 'community' to 'general'
                    // contextParams = {
                    //     communityIds: selectedTrustCircles.map(circle => circle.id)
                    // };
                }
            }
            
            // Add search context parameters
            params.set('search_context', searchContext);
            if (Object.keys(contextParams).length > 0) {
                params.set('context_params', JSON.stringify(contextParams));
            }
            
            const searchUrl = `${API_URL}/api/providers/search?${params.toString()}`;
            const response = await fetch(searchUrl);
            const responseBody = await response.text();
            if (!response.ok) { let errP; try { errP = JSON.parse(responseBody); } catch (pE) { throw new Error(`HTTP error! status: ${response.status}, Non-JSON response: ${responseBody}`); } throw new Error(errP.message || errP.error || `HTTP error! ${response.status}`); }
            const d = JSON.parse(responseBody);
            if (d.success) {
                const base = `/search?q=${encodeURIComponent(q)}&state=${encodeURIComponent(finalState)}`; // Pass state to URL
                navigate(d.providers?.length > 0 ? base : base + "&noResults=true", {
                    state: { initialProviders: d.providers, currentSearchUserId: dbUser.clerkId },
                });
            } else { throw new Error(d.message || d.error || "Search not successful"); }
        } catch (err) { console.error("Search error:", err); }
        finally { setIsSearching(false); }
    };

    const handleLocationClick = () => setShowLocationModal(true);

    // Notification handlers
    const handleNotificationClick = () => {
        setIsNotificationModalOpen(true);
        if (notifications.length === 0) {
            fetchNotifications();
        }
    };
    const handleCloseNotificationModal = () => setIsNotificationModalOpen(false);
    
    // Filter handling functions
    const handleFilterModeChange = (mode) => {
        setCurrentFilterMode(mode);
        setShowMainFilterDropdown(false);
        if (mode === 'specific') {
            // Don't automatically show trust circle dropdown for specific mode
            // It will be shown on hover instead
        } else {
            setShowTrustCircleDropdown(false);
            setSelectedTrustCircles([]);
        }
    };

    const handleTrustCircleSelection = (circleId, circleName) => {
        setSelectedTrustCircles(prev => {
            const isSelected = prev.find(circle => circle.id === circleId);
            if (isSelected) {
                // Remove if already selected
                const filtered = prev.filter(circle => circle.id !== circleId);
                if (filtered.length === 0) {
                    // If no circles selected, go back to "all" mode
                    setCurrentFilterMode('all');
                    setShowTrustCircleDropdown(false);
                }
                return filtered;
            } else {
                // Add if not selected
                return [...prev, { id: circleId, name: circleName }];
            }
        });
    };



    // Handle clicking outside the dropdowns to close them
    const handleClickOutside = (event) => {
        const trustCircleDropdown = event.target.closest('.trust-circle-dropdown');
        const mainFilterDropdown = event.target.closest('.main-filter-dropdown');
        const filterButton = event.target.closest('.filter-chip');
        const mainFilterButton = event.target.closest('.main-filter-button');
        const specificTrustCirclesItem = event.target.closest('.specific-trust-circles');
        
        if (!trustCircleDropdown && !filterButton && !specificTrustCirclesItem && showTrustCircleDropdown) {
            setShowTrustCircleDropdown(false);
        }
        
        if (!mainFilterDropdown && !mainFilterButton && showMainFilterDropdown) {
            setShowMainFilterDropdown(false);
        }
    };

    // Leaderboard click handler
    const handleLeaderboardUserClick = (user) => {
        if (user.username) {
            navigate(`/pro/${user.username}`);
        }
    };

    // Batch fetch comments for multiple recommendations
    const fetchBatchComments = async (recommendations, setCommentsMap) => {
        if (!recommendations || recommendations.length === 0) return;
        
        setIsLoadingComments(true);
        try {
            const serviceIds = recommendations.map(rec => rec.provider_id || rec.id).filter(Boolean);
            
            if (serviceIds.length === 0) return;

            const response = await fetch(`${API_URL}/api/comments/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ service_ids: serviceIds })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.comments) {
                    const commentsMap = new Map();
                    Object.entries(data.comments).forEach(([serviceId, comments]) => {
                        commentsMap.set(serviceId, comments || []);
                    });
                    setCommentsMap(commentsMap);
                }
            }
        } catch (error) {
            console.error('Error fetching batch comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    // Handle new comment added
    const handleCommentAdded = (serviceId, newComment) => {
        // Update both maps in case the service appears in both lists
        setRecentCommentsMap(prev => {
            const newMap = new Map(prev);
            const existingComments = newMap.get(serviceId) || [];
            newMap.set(serviceId, [newComment, ...existingComments]);
            return newMap;
        });
        
        setPublicCommentsMap(prev => {
            const newMap = new Map(prev);
            const existingComments = newMap.get(serviceId) || [];
            newMap.set(serviceId, [newComment, ...existingComments]);
            return newMap;
        });
    };
    
    // Calculate user level and progress
    const calculateLevel = (score) => {
        const level = Math.floor(score / 100);
        const progress = score % 100;
        return { level, progress };
    };

    // Typing animation logic
    const targetText = useMemo(() => {
        if (!isLoaded) return `Welcome to ${BRAND_PHRASE}`;
        if (!isSignedIn) return `Welcome to ${BRAND_PHRASE}`;
        return preferredName || user?.firstName ? `Welcome back, ${preferredName || user.firstName}.` : `Welcome to ${BRAND_PHRASE}`;
    }, [isLoaded, isSignedIn, preferredName, user?.firstName]);

    useEffect(() => {
        setDisplayText(""); 
        setIsTyping(true); 
        setShowStatsLine(false);
    }, [targetText]);

    useEffect(() => {
        if (!isTyping || !targetText) { 
            if (!targetText) setIsTyping(false); 
            return; 
        }
        if (displayText.length < targetText.length) {
            const next = targetText.substring(0, displayText.length + 1);
            const t = setTimeout(() => setDisplayText(next), 100);
            return () => clearTimeout(t);
        } else { 
            setIsTyping(false); 
        }
    }, [displayText, isTyping, targetText]);

    useEffect(() => {
        if (!isTyping && displayText === targetText && displayText !== "") {
            if (isSignedIn) setShowStatsLine(true);
        } else { 
            setShowStatsLine(false); 
        }
    }, [isTyping, displayText, targetText, isSignedIn]);

    // User mention handling functions
    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        const cursorPosition = e.target.selectionStart;
        setSearchQuery(value);

        // Auto-resize textarea
        if (searchInputElementRef.current) {
            searchInputElementRef.current.style.height = 'auto';
            // Allow more height on mobile for better text wrapping
            const maxHeight = isMobile ? 200 : 120;
            searchInputElementRef.current.style.height = Math.min(searchInputElementRef.current.scrollHeight, maxHeight) + 'px';
        }

        // Check if we should show mention dropdown
        const searchTerm = getMentionSearchTerm(value, cursorPosition);
        
        if (searchTerm !== null) {
            setMentionSearchTerm(searchTerm);
            
            // On mobile, add a small delay to ensure positioning works correctly
            if (isMobile) {
                setTimeout(() => {
                    setShowMentionDropdown(true);
                }, 50);
            } else {
                setShowMentionDropdown(true);
            }
            
            // Calculate dropdown position
            if (searchInputElementRef.current) {
                const inputStyles = window.getComputedStyle(searchInputElementRef.current);
                const paddingLeft = parseFloat(inputStyles.paddingLeft) || 0;
                const borderLeft = parseFloat(inputStyles.borderLeftWidth) || 0;
                
                // Create a temporary span to measure text width
                const tempSpan = document.createElement('span');
                tempSpan.style.font = inputStyles.font;
                tempSpan.style.visibility = 'hidden';
                tempSpan.style.position = 'absolute';
                tempSpan.style.whiteSpace = 'pre';
                tempSpan.textContent = value.substring(0, cursorPosition);
                document.body.appendChild(tempSpan);
                
                const textWidth = tempSpan.offsetWidth;
                document.body.removeChild(tempSpan);
                
                // Get the input's position
                const inputRect = searchInputElementRef.current.getBoundingClientRect();
                
                // For mobile, use a simpler, more reliable positioning approach
                if (isMobile) {
                    // Position dropdown below the cursor on mobile, similar to desktop
                    const calculateMobilePosition = () => {
                        // Recalculate input position after any viewport changes
                        const updatedInputRect = searchInputElementRef.current.getBoundingClientRect();
                        
                        // Recalculate text width to cursor position for accurate positioning
                        const inputStyles = window.getComputedStyle(searchInputElementRef.current);
                        const paddingLeft = parseFloat(inputStyles.paddingLeft) || 0;
                        const borderLeft = parseFloat(inputStyles.borderLeftWidth) || 0;
                        
                        // Create temporary span to measure text width to cursor
                        const tempSpan = document.createElement('span');
                        tempSpan.style.font = inputStyles.font;
                        tempSpan.style.visibility = 'hidden';
                        tempSpan.style.position = 'absolute';
                        tempSpan.style.whiteSpace = 'pre';
                        tempSpan.textContent = value.substring(0, cursorPosition);
                        document.body.appendChild(tempSpan);
                        
                        const textWidthToCursor = tempSpan.offsetWidth;
                        document.body.removeChild(tempSpan);
                        
                        const dropdownWidth = Math.min(280, window.innerWidth - 32);
                        const dropdownHeight = 160; // Slightly smaller for mobile
                        
                        // Position dropdown at cursor position horizontally
                        let leftPos = updatedInputRect.left + paddingLeft + borderLeft + textWidthToCursor;
                        
                        // Ensure it doesn't go off-screen horizontally
                        if (leftPos + dropdownWidth > window.innerWidth - 16) {
                            leftPos = window.innerWidth - dropdownWidth - 16;
                        }
                        if (leftPos < 16) leftPos = 16;
                        
                        // Position below the cursor line
                        // For mobile, we need to account for the current line height
                        const lineHeight = parseFloat(inputStyles.lineHeight) || 24;
                        let topPos = updatedInputRect.top + lineHeight + 8;
                        
                        // Get current viewport height (accounting for keyboard)
                        const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
                        
                        // If dropdown would go below visible area, position above the cursor line
                        if (topPos + dropdownHeight > viewportHeight - 16) {
                            topPos = updatedInputRect.top - dropdownHeight - 8;
                        }
                        
                        // Final boundary check
                        if (topPos < 16) topPos = 16;
                        
                        setMentionDropdownPosition({
                            top: topPos,
                            left: leftPos
                        });
                    };
                    
                    // Use immediate calculation for above-input positioning
                    // Add small delay only to account for any zoom effects
                    setTimeout(calculateMobilePosition, 150);
                } else {
                    // For desktop, use fixed positioning like mobile for consistency
                    const dropdownWidth = 320; // Fixed width for desktop
                    const dropdownHeight = 200;
                    
                    // Position dropdown right below the input at cursor position
                    let leftPos = inputRect.left + paddingLeft + borderLeft + textWidth;
                    
                    // Ensure it doesn't go off-screen horizontally
                    if (leftPos + dropdownWidth > window.innerWidth - 16) {
                        leftPos = window.innerWidth - dropdownWidth - 16;
                    }
                    if (leftPos < 16) leftPos = 16;
                    
                    // Position below input, or above if not enough space
                    let topPos = inputRect.bottom + 8;
                    if (topPos + dropdownHeight > window.innerHeight - 16) {
                        topPos = inputRect.top - dropdownHeight - 8;
                    }
                    
                    // Ensure minimum distance from top
                    if (topPos < 16) topPos = 16;
                    
                    setMentionDropdownPosition({
                        top: topPos,
                        left: leftPos
                    });
                }
            }
        } else {
            setShowMentionDropdown(false);
        }
    };

    const handleSearchInputKeyDown = (e) => {
        if (showMentionDropdown) {
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowMentionDropdown(false);
                return;
            }
        }
        
        // Handle Enter key for desktop search submission
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSearch(e);
        }
    };

    const handleMentionSelect = (selectedUser) => {
        const currentQuery = searchQuery;
        const cursorPosition = searchInputElementRef.current?.selectionStart || 0;
        
        // Find the @ symbol position
        let atSymbolIndex = cursorPosition - 1;
        while (atSymbolIndex >= 0 && currentQuery[atSymbolIndex] !== '@') {
            atSymbolIndex--;
        }
        
        if (atSymbolIndex >= 0) {
            // Replace the mention with the selected user
            const beforeMention = currentQuery.substring(0, atSymbolIndex);
            const afterMention = currentQuery.substring(cursorPosition);
            const username = selectedUser.username || selectedUser.name;
            const newQuery = beforeMention + `@${username} ` + afterMention;
            
            setSearchQuery(newQuery);
            setShowMentionDropdown(false);
            
            // Set cursor position after the mention
            setTimeout(() => {
                if (searchInputElementRef.current) {
                    const newCursorPosition = atSymbolIndex + username.length + 2; // +2 for @ and space
                    searchInputElementRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
                    searchInputElementRef.current.focus();
                }
            }, 0);
        }
    };

    const handleMentionDropdownClose = () => {
        setShowMentionDropdown(false);
    };

    // Function to handle hover over existing mentions
    const handleMentionHover = (username, event) => {
        const rect = event.target.getBoundingClientRect();
        setHoveredMention(username);
        setMentionHoverPosition({
            top: rect.bottom + 5,
            left: rect.left
        });
    };

    const handleMentionLeave = () => {
        setHoveredMention(null);
    };

    // Function to get filter mode display text
    const getFilterModeDisplayText = () => {
        switch (currentFilterMode) {
            case 'all':
                return 'All Recs';
            case 'myTrust':
                return 'My Trust Circle';
            case 'specific':
                return 'Specific Trust Circles';
            default:
                return 'All Recs';
        }
    };

    // Hover handlers for specific trust circles
    const handleSpecificTrustCirclesHover = () => {
        setShowTrustCircleDropdown(true);
    };

    const handleSpecificTrustCirclesLeave = () => {
        // Don't close immediately - let the submenu handle its own hover
        setTimeout(() => {
            if (!document.querySelector('.trust-circle-hover-dropdown:hover')) {
                setShowTrustCircleDropdown(false);
            }
        }, 100);
    };

    const handleTrustCircleDropdownHover = () => {
        setShowTrustCircleDropdown(true);
    };

    const handleTrustCircleDropdownLeave = () => {
        setShowTrustCircleDropdown(false);
    };

    const handleSpecificTrustCirclesClick = () => {
        handleFilterModeChange('specific');
    };

    // Function to style user mentions in the search query with hover functionality
    const styleMentions = (text) => {
        if (!text) return text;
        
        // Regular expression to match @username patterns
        const mentionRegex = /@(\w+)/g;
        
        return text.replace(mentionRegex, (match, username) => {
            return `<span class="user-mention" data-username="${username}" onmouseover="window.handleMentionHover('${username}', event)" onmouseout="window.handleMentionLeave()">${match}</span>`;
        });
    };

    if (location.pathname !== "/") return null;

    if (!isLoaded) {
        // return <div className="loading-full-page">Building your Hyve...</div>;
    }

    // Dashboard layout for signed-in users
    if (isSignedIn) {
        return (
            <div className="home dashboard-layout">
                <Particles
                    id="tsparticles"
                    init={particlesInit}
                    options={particlesOptions}
                />
                <div className="dashboard-container">
                    {/* Left Column */}
                    <div className="dashboard-left">
                        {/* Mobile Landing Page for Logged-in Users */}
                        {isMobile && (
                            <div className="mobile-landing-page">
                                {/* Quick Stats Badge */}
                                <div className="mobile-stats-badge">
                                    <span className="stats-text">
                                        {isLoadingCounts ? (
                                            "Loading stats..."
                                        ) : (
                                            <>
                                                {providerCount || 0} Recs for You • {connectionCount || 0} Followers
                                            </>
                                        )}
                                    </span>
                                </div>

                                {/* Main Heading */}
                                <h1 className="mobile-main-heading">
                                    What would you like to <strong>discover today?</strong>
                                </h1>
                                
                                <p className="mobile-subtitle">
                                    Search, <strong style={{color: '#ffb72e'}}>Powered by People You Trust</strong>
                                </p>

                                {/* Main Search Area */}
                                <div className="mobile-search-container">
                                    <div className="mobile-search-input-group">
                                        <textarea 
                                            ref={searchInputElementRef}
                                            className="mobile-main-search-input" 
                                            placeholder="Ask Anything, @ to search a friend's Recs..." 
                                            value={searchQuery} 
                                            onChange={handleSearchInputChange} 
                                            onKeyDown={handleSearchInputKeyDown}
                                            disabled={isSearching} 
                                            rows="1"
                                        />
                                        
                                        {/* User Mention Dropdown for Mobile */}
                                        <UserMentionDropdown
                                            isVisible={showMentionDropdown}
                                            searchTerm={mentionSearchTerm}
                                            onSelectUser={handleMentionSelect}
                                            onClose={handleMentionDropdownClose}
                                            position={mentionDropdownPosition}
                                            currentUserId={user?.id || dbUser?.clerkId}
                                        />
                                        
                                        {/* Hover Tooltip for Existing Mentions */}
                                        {hoveredMention && (
                                            <div 
                                                className="mention-hover-tooltip"
                                                style={{
                                                    position: 'fixed',
                                                    top: mentionHoverPosition.top,
                                                    left: mentionHoverPosition.left,
                                                    zIndex: 1001
                                                }}
                                            >
                                                <div className="tooltip-content">
                                                    <div className="tooltip-avatar">
                                                        {hoveredMention.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="tooltip-info">
                                                        <div className="tooltip-name">{hoveredMention}</div>
                                                        <div className="tooltip-username">@{hoveredMention}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Bottom Controls Row */}
                                        <div className="mobile-search-bottom-controls">
                                            <div 
                                                className="mobile-location-info"
                                                onClick={() => {
                                                    setLocationInput(`${city}, ${state}`);
                                                    setIsEditingLocation(true);
                                                }}
                                            >
                                                <MapPinIcon className="mobile-location-icon" />
                                                <span className="mobile-location-text">{city && state ? `${city}, ${state}` : 'Set Location'}</span>
                                            </div>
                                            
                                            <div className="mobile-bottom-right">
                                                <button 
                                                    type="button" 
                                                    className="mobile-filter-dropdown-btn"
                                                    onClick={() => setShowMainFilterDropdown(!showMainFilterDropdown)}
                                                >
                                                    {getFilterModeDisplayText()}
                                                    <span className="dropdown-arrow">▼</span>
                                                </button>
                                                
                                                <button 
                                                    type="submit" 
                                                    className="mobile-search-btn"
                                                    disabled={isSearching}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleSearch(e);
                                                    }}
                                                >
                                                    <MagnifyingGlassIcon className="mobile-search-icon" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Popular Searches */}
                                <div className="mobile-popular-searches">
                                    <h3 className="mobile-popular-title">Trending searches in your circle</h3>
                                    <div className="mobile-search-suggestions">
                                        <div 
                                            className="mobile-search-suggestion"
                                            onClick={() => {
                                                console.log('Mobile popular search clicked: @venkat');
                                                setSearchQuery("What's @venkat's favorite Thai place in Seattle");
                                                setTimeout(() => {
                                                    console.log('Calling handleSearch for @venkat');
                                                    handleSearch(null, "What's @venkat's favorite Thai place in Seattle");
                                                }, 100);
                                            }}
                                        >
                                            <span className="suggestion-text">"What's @venkat's favorite Thai place in Seattle"</span>
                                            <MagnifyingGlassIcon className="suggestion-icon" />
                                        </div>
                                        <div 
                                            className="mobile-search-suggestion"
                                            onClick={() => {
                                                console.log('Mobile popular search clicked: Kati Vegan Thai');
                                                setSearchQuery("Kati Vegan Thai");
                                                setTimeout(() => {
                                                    console.log('Calling handleSearch for Kati Vegan Thai');
                                                    handleSearch(null, "Kati Vegan Thai");
                                                }, 100);
                                            }}
                                        >
                                            <span className="suggestion-text">"Kati Vegan Thai"</span>
                                            <MagnifyingGlassIcon className="suggestion-icon" />
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile Filter Dropdown */}
                                {showMainFilterDropdown && (
                                    <div className="mobile-filter-dropdown-overlay" onClick={() => setShowMainFilterDropdown(false)}>
                                        <div className="mobile-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                                            <div className="mobile-filter-dropdown-header">
                                                <h3>Filter Recommendations</h3>
                                                <button 
                                                    className="mobile-filter-close-btn"
                                                    onClick={() => setShowMainFilterDropdown(false)}
                                                >
                                                    <XMarkIcon className="mobile-filter-close-icon" />
                                                </button>
                                            </div>
                                            <div className="mobile-filter-options">
                                                <button 
                                                    type="button" 
                                                    className={`mobile-filter-option ${currentFilterMode === 'all' ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleFilterModeChange('all');
                                                        setShowMainFilterDropdown(false);
                                                    }}
                                                >
                                                    All Recs
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className={`mobile-filter-option ${currentFilterMode === 'myTrust' ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleFilterModeChange('myTrust');
                                                        setShowMainFilterDropdown(false);
                                                    }}
                                                >
                                                    My Trust Circle
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className={`mobile-filter-option ${currentFilterMode === 'specific' ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleSpecificTrustCirclesClick();
                                                        setShowMainFilterDropdown(false);
                                                    }}
                                                >
                                                    Specific Trust Circles
                                                </button>
                                                {selectedTrustCircles.length > 0 && (
                                                    <div className="mobile-selected-trust-circles">
                                                        <div className="mobile-selected-header">Selected Trust Circles:</div>
                                                        {selectedTrustCircles.map((circle) => (
                                                            <div key={circle.id} className="mobile-selected-circle">
                                                                {circle.name}
                                                                <button 
                                                                    className="mobile-remove-circle"
                                                                    onClick={() => handleTrustCircleSelection(circle.id, circle.name)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Floating Action Button */}
                                <button className="mobile-fab" onClick={() => navigate('/share-recommendation')}>
                                    <PlusIcon className="mobile-fab-icon" />
                                </button>
                            </div>
                        )}

                        {/* Desktop Layout - Unchanged */}
                        {!isMobile && (
                            <>
                                {/* Welcome Message */}
                        <div className="dashboard-welcome">
                            <h1 className="dashboard-title">
                                {displayText}
                            </h1>
                            <p className="dashboard-subtitle">
                                {isLoadingNewRecsCount || isLoadingNotificationsCount ? (
                                    "Loading your latest updates..."
                                ) : (
                                    (() => {
                                        const hasNewRecs = newRecsCount > 0;
                                        const hasNotifications = newNotificationsCount > 0;

                                        if (!hasNewRecs && !hasNotifications) {
                                            return (
                                                <>
                                                    Check out anything cool recently?{" "}
                                                    <span 
                                                        className="underline-highlight"
                                                        onClick={() => navigate('/share-recommendation')}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        Share it with your Trust Circle
                                                    </span>.
                                                </>
                                            );
                                        } else if (hasNewRecs && hasNotifications) {
                                            // Both recs and notifications
                                            return (
                                                <>
                                                    Let's get you caught up. You have{" "}
                                                    <strong>{newRecsCount} new Rec{newRecsCount === 1 ? '' : 's'}</strong> and{" "}
                                                    <strong 
                                                        className="notification-link" 
                                                        onClick={handleNotificationClick}
                                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {newNotificationsCount} notification{newNotificationsCount === 1 ? '' : 's'}
                                                    </strong>!
                                                </>
                                            );
                                        } else if (hasNewRecs && !hasNotifications) {
                                            // Only recs
                                            return (
                                                <>
                                                    Let's get you caught up. You have{" "}
                                                    <strong>{newRecsCount} new Rec{newRecsCount === 1 ? '' : 's'}</strong>!
                                                </>
                                            );
                                        } else {
                                            // Only notifications
                                            return (
                                                <>
                                                    Let's get you caught up. You have{" "}
                                                    <strong 
                                                        className="notification-link" 
                                                        onClick={handleNotificationClick}
                                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {newNotificationsCount} notification{newNotificationsCount === 1 ? '' : 's'}
                                                    </strong>!
                                                </>
                                            );
                                        }
                                    })()
                                )}
                            </p>
                        </div>

                        {/* Search Bar */}
                        <form className="dashboard-search-form dashboard-search-expansive" onSubmit={handleSearch}>
                            {/* Main Search Input */}
                            <div className="search-input-group-expansive">
                                <div className="search-input-main">
                                    <textarea 
                                        ref={searchInputElementRef}
                                        className="main-search-input-expansive" 
                                        placeholder="Ask anything and @mention a friend to search their Recs..." 
                                        value={searchQuery} 
                                        onChange={handleSearchInputChange} 
                                        onKeyDown={handleSearchInputKeyDown}
                                        disabled={isSearching} 
                                        rows="1"
                                        style={{ resize: 'none', overflow: 'hidden' }}
                                    />
                                    <div className="search-actions">
                                        <button type="submit" className="search-submit-button-expansive" disabled={isSearching}> 
                                            {isSearching ? <span className="search-spinner"></span> : <MagnifyingGlassIcon className="search-button-icon" />} 
                                        </button>
                                    </div>
                                </div>
                                
                                {/* User Mention Dropdown */}
                                <UserMentionDropdown
                                    isVisible={showMentionDropdown}
                                    searchTerm={mentionSearchTerm}
                                    onSelectUser={handleMentionSelect}
                                    onClose={handleMentionDropdownClose}
                                    position={mentionDropdownPosition}
                                    currentUserId={user?.id || dbUser?.clerkId}
                                />
                                
                                {/* Hover Tooltip for Existing Mentions */}
                                {hoveredMention && (
                                    <div 
                                        className="mention-hover-tooltip"
                                        style={{
                                            position: 'fixed',
                                            top: mentionHoverPosition.top,
                                            left: mentionHoverPosition.left,
                                            zIndex: 1001
                                        }}
                                    >
                                        <div className="tooltip-content">
                                            <div className="tooltip-avatar">
                                                {hoveredMention.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="tooltip-info">
                                                <div className="tooltip-name">{hoveredMention}</div>
                                                <div className="tooltip-username">@{hoveredMention}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Location and Trust Circles Row */}
                                <div className="search-filters-row">
                                    <div className="location-input-wrapper-expansive">
                                        <MapPinIcon className="location-icon" />
                                        {isEditingLocation ? (
                                            <input
                                                className="location-input-expansive"
                                                type="text"
                                                value={locationInput}
                                                onChange={e => setLocationInput(e.target.value)}
                                                onBlur={handleUpdateLocation}
                                                onKeyDown={(e) => e.key === 'Enter' && handleUpdateLocation()}
                                                autoFocus
                                            />
                                        ) : (
                                            <>
                                                <span className="location-text">{city && state ? `${city}, ${state}` : 'Your Location'}</span>
                                                <button type="button" className="location-change-button" onClick={() => {
                                                    setLocationInput(`${city}, ${state}`);
                                                    setIsEditingLocation(true);
                                                }}>
                                                    Change
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    
                                    <div className="search-quick-filters">
                                        {isLoadingTrustCircles ? (
                                            <div className="filter-chip loading-spinner"></div>
                                        ) : trustCircles.length > 0 ? (
                                            <>
                                                {/* Main filter dropdown */}
                                                <div className="main-filter-container">
                                                    <button 
                                                        type="button" 
                                                        className={`main-filter-button ${currentFilterMode ? 'active' : ''}`}
                                                        onClick={() => setShowMainFilterDropdown(!showMainFilterDropdown)}
                                                    >
                                                        <span>{getFilterModeDisplayText()}</span>
                                                        <span className="dropdown-arrow">▼</span>
                                                    </button>
                                                    
                                                    {showMainFilterDropdown && (
                                                        <div className="main-filter-dropdown">
                                                            <button 
                                                                type="button" 
                                                                className={`dropdown-item ${currentFilterMode === 'all' ? 'selected' : ''}`}
                                                                onClick={() => handleFilterModeChange('all')}
                                                            >
                                                                All Recs
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                className={`dropdown-item ${currentFilterMode === 'myTrust' ? 'selected' : ''}`}
                                                                onClick={() => handleFilterModeChange('myTrust')}
                                                            >
                                                                My Trust Circle
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                className={`dropdown-item specific-trust-circles ${currentFilterMode === 'specific' ? 'selected' : ''}`}
                                                                onClick={handleSpecificTrustCirclesClick}
                                                                onMouseEnter={handleSpecificTrustCirclesHover}
                                                                onMouseLeave={handleSpecificTrustCirclesLeave}
                                                            >
                                                                Specific Trust Circles
                                                                <span className="dropdown-arrow">▶</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Selected specific trust circles */}
                                                {selectedTrustCircles.length > 0 && (
                                                    selectedTrustCircles.map((circle) => (
                                                        <button 
                                                            key={circle.id} 
                                                            type="button" 
                                                            className="filter-chip selected" 
                                                            onClick={() => handleTrustCircleSelection(circle.id, circle.name)}
                                                        >
                                                            {circle.name}
                                                            <span className="remove-chip">×</span>
                                                        </button>
                                                    ))
                                                )}
                                                
                                                {/* Dropdown for specific trust circles */}
                                                {showTrustCircleDropdown && (
                                                    <div 
                                                        className="trust-circle-dropdown trust-circle-hover-dropdown"
                                                        onMouseEnter={handleTrustCircleDropdownHover}
                                                        onMouseLeave={handleTrustCircleDropdownLeave}
                                                    >
                                                        <div className="dropdown-header">
                                                            Select Trust Circles:
                                                        </div>
                                                        {trustCircles.map((circle) => {
                                                            const isSelected = selectedTrustCircles.find(selected => selected.id === circle.id);
                                                            return (
                                                                <button 
                                                                    key={circle.id} 
                                                                    type="button" 
                                                                    className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                                                                    onClick={() => handleTrustCircleSelection(circle.id, circle.name)}
                                                                >
                                                                    {circle.name}
                                                                    {isSelected && <span className="checkmark">✓</span>}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <button 
                                                type="button" 
                                                className="filter-chip build-trust-circle"
                                                onClick={() => navigate('/trustcircles')}
                                            >
                                                Build Your Trust Circle
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* AI Suggestions */}
                                <div className="search-suggestions">
                                    <div className="suggestion-chips">
                                        <span className="suggestion-label">Trending Searches:</span>
                                        <button 
                                            type="button" 
                                            className="suggestion-chip"
                                            onClick={() => {
                                                setSearchQuery("What's @venkat's favorite Thai place in Seattle");
                                                setTimeout(() => {
                                                    const form = document.querySelector('.dashboard-search-form');
                                                    if (form) {
                                                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                                    }
                                                }, 100);
                                            }}
                                        >
                                            "What's @venkat's favorite Thai place in Seattle"
                                        </button>
                                        <button 
                                            type="button" 
                                            className="suggestion-chip"
                                            onClick={() => {
                                                setSearchQuery("Kati Vegan Thai");
                                                setTimeout(() => {
                                                    const form = document.querySelector('.dashboard-search-form');
                                                    if (form) {
                                                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                                    }
                                                }, 100);
                                            }}
                                        >
                                            "Kati Vegan Thai"
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Mobile Location Info Below Search */}
                            {isMobile && (
                                isEditingLocation ? (
                                    <div className="mobile-location-edit-wrapper">
                                        <input
                                            className="location-input mobile-edit"
                                            type="text"
                                            value={locationInput}
                                            onChange={e => setLocationInput(e.target.value)}
                                            onBlur={handleUpdateLocation}
                                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateLocation()}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="location-save-button"
                                            onClick={handleUpdateLocation}
                                        >
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mobile-location-info">
                                        <MapPinIcon className="location-icon" />
                                        <span>{city && state ? `${city}, ${state}` : "Set Location"}</span>
                                        <button type="button" className="mobile-location-change" onClick={() => {
                                            setLocationInput(`${city}, ${state}`);
                                            setIsEditingLocation(true);
                                        }}>
                                            Change
                                        </button>
                                    </div>
                                )
                            )}
                        </form>



                        {/* Desktop Recent Recommendations */}
                        {!isMobile && (
                            <div className="dashboard-recommendations">
                                <h2 className="section-title">3 Recent Recommendations For You</h2>
                                <div className="recommendations-feed">
                                    {isLoadingRecentRecommendations && <div className="feed-message">Loading recommendations...</div>}
                                    {recentRecommendationsError && <div className="feed-message feed-error">Could not load recommendations.</div>}
                                    {!isLoadingRecentRecommendations && !recentRecommendationsError && recentRecommendations.length > 0 ? (
                                        <>
                                            {recentRecommendations.map((rec) => (
                                                <RecommendationCard
                                                    key={rec.id}
                                                    rec={rec}
                                                    onWriteReview={handleOpenReviewModal}
                                                    onLike={handleLikeRecommendation}
                                                    isLikedByCurrentUser={likedRecommendations.has(rec.id)}
                                                    loggedInUserId={user?.id}
                                                    currentUserName={user?.firstName}
                                                    comments={recentCommentsMap.get(String(rec.provider_id || rec.id)) || []}
                                                    onCommentAdded={handleCommentAdded}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        !isLoadingRecentRecommendations && !recentRecommendationsError && (
                                            <div className="feed-message">No recent recommendations for you yet.</div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                            </>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="dashboard-right">
                        {/* Score Wheel - Hidden on Mobile */}
                        {!isMobile && (
                            <TrustScoreWheel 
                                score={userScore} 
                                showDebug={false}
                            />
                        )}

                        {/* Mobile Recent Recommendations - Replaces Score Wheel */}
                        {isMobile && (
                            <div className="dashboard-recommendations mobile-recommendations">
                                <h2 className="section-title">3 Recent Recommendations For You</h2>
                                <div className="recommendations-feed">
                                    {isLoadingRecentRecommendations && <div className="feed-message">Loading recommendations...</div>}
                                    {recentRecommendationsError && <div className="feed-message feed-error">Could not load recommendations.</div>}
                                    {!isLoadingRecentRecommendations && !recentRecommendationsError && recentRecommendations.length > 0 ? (
                                        <>
                                            {recentRecommendations.slice(0, 3).map((rec) => (
                                                <RecommendationCard
                                                    key={rec.id}
                                                    rec={rec}
                                                    onWriteReview={handleOpenReviewModal}
                                                    onLike={handleLikeRecommendation}
                                                    isLikedByCurrentUser={likedRecommendations.has(rec.id)}
                                                    loggedInUserId={user?.id}
                                                    currentUserName={user?.firstName}
                                                    comments={recentCommentsMap.get(String(rec.provider_id || rec.id)) || []}
                                                    onCommentAdded={handleCommentAdded}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        !isLoadingRecentRecommendations && !recentRecommendationsError && (
                                            <div className="feed-message">No recent recommendations for you yet.</div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Stats Cards */}
                        {!isMobile && (
                            <div className="dashboard-stats">
                                <button className="metric-card" onClick={() => navigate("/trustcircles?tab=for-you")}>
                                    <div className="metric-icon-container">
                                        {isLoadingCounts ? (
                                            <div className="metric-loading-spinner"></div>
                                        ) : (
                                            <div className="metric-number"><CountUp end={providerCount || 0} duration={2} separator="," /></div>
                                        )}
                                    </div>
                                    <div className="metric-content">
                                        <div className="metric-label">Recommendations</div>
                                        <div className="metric-sublabel">for you</div>
                                    </div>
                                </button>
                                <button className="metric-card" onClick={() => navigate("/trustcircles?tab=myTrust")}>
                                    <div className="metric-icon-container">
                                        {isLoadingCounts ? (
                                            <div className="metric-loading-spinner"></div>
                                        ) : (
                                            <div className="metric-number"><CountUp end={connectionCount || 0} duration={2} /></div>
                                        )}
                                    </div>
                                    <div className="metric-content">
                                        <div className="metric-label">Followers</div>
                                    </div>
                                </button>
                                <button className="metric-card" onClick={() => navigate("/trustcircles?tab=communities")}>
                                    <div className="metric-icon-container">
                                        {isLoadingCounts ? (
                                            <div className="metric-loading-spinner"></div>
                                        ) : (
                                            <div className="metric-number"><CountUp end={communityCount || 0} duration={2} /></div>
                                        )}
                                    </div>
                                    <div className="metric-content">
                                        <div className="metric-label">Communities</div>
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Leaderboard */}
                        <div className="leaderboard-container">
                            <h3 className="section-title">Your Trust Circle Leaderboard</h3>
                            <div className="leaderboard">
                                {isLoadingLeaderboard ? (
                                    <div className="leaderboard-loading">
                                        <div className="metric-loading-spinner"></div>
                                        <span>Loading leaderboard...</span>
                                    </div>
                                ) : leaderboardData.length > 0 ? (
                                    leaderboardData.map((user, index) => {
                                        if (user.isSeparator) {
                                            return (
                                                <div key="separator" className="leaderboard-separator">
                                                    <div className="separator-dots">⋮</div>
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <div 
                                                key={user.rank || index} 
                                                className={`leaderboard-item ${user.isCurrentUser ? 'current-user' : ''} ${user.username ? 'clickable' : ''}`}
                                                onClick={() => handleLeaderboardUserClick(user)}
                                                style={{ cursor: user.username ? 'pointer' : 'default' }}
                                            >
                                                <div className="rank">#{user.rank}</div>
                                                <div className="avatar">{user.avatar}</div>
                                                <div className="user-info">
                                                    <div className="name">{user.name}</div>
                                                    <div className="score">{user.score} pts</div>
                                                </div>
                                                {user.rank <= 3 && (
                                                    <div className="trophy">
                                                        {user.rank === 1 ? '🥇' : user.rank === 2 ? '🥈' : '🥉'}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="leaderboard-empty">
                                        <p>Connect with others to see the leaderboard!</p>
                                        <button 
                                            className="button-primary"
                                            onClick={() => navigate("/trustcircles")}
                                        >
                                            Find Connections
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <ReviewModal
                    isOpen={isReviewModalOpen}
                    onClose={handleCloseReviewModal}
                    onSubmit={handleSubmitReview}
                    provider={providerForReview}
                />

                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    message={successMessage}
                />

                <NotificationModal
                    isOpen={isNotificationModalOpen}
                    onClose={handleCloseNotificationModal}
                    notifications={notifications}
                    isLoading={isLoadingNotifications}
                    onMarkAsRead={handleMarkNotificationAsRead}
                    onMarkAllAsRead={handleMarkAllNotificationsAsRead}
                />

                {showLocationModal && (
                    <div className="location-modal-overlay">
                        <div className="location-modal-content">
                            <button className="location-modal-close" onClick={() => setShowLocationModal(false)}> <XMarkIcon /> </button>
                            <h3>Expanding Our Horizons!</h3>
                            <p>We're currently focused on serving the <strong>Greater Seattle Area</strong>.</p>
                            <p>We're working hard to expand and will be launching nationally soon. Stay tuned!</p>
                        </div>
                    </div>
                )}

                {showMobileAuthModal && (
                    <div className="mobile-auth-modal-overlay">
                        <div className="mobile-auth-modal">
                            <button className="mobile-auth-close" onClick={() => setShowMobileAuthModal(false)}> <XMarkIcon /> </button>
                            <h3>Join to Search</h3>
                            <p>Sign up or sign in to search recommendations from people you trust.</p>
                            <div className="mobile-auth-buttons">
                                <SignUpButton mode="modal">
                                    <button className="button-primary">Sign Up</button>
                                </SignUpButton>
                                <SignInButton mode="modal">
                                    <button className="button-secondary">Already have an account? Sign In</button>
                                </SignInButton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Dashboard layout for non-signed-in users
    return (
        <div className="home dashboard-layout not-logged-in">
            <Particles
                id="tsparticles"
                init={particlesInit}
                options={particlesOptions}
            />
            <div className="dashboard-container">
                {/* Left Column */}
                <div className="dashboard-left">
                    {isMobile ? (
                        // Mobile Landing Page for Not-Logged-In Users
                        <div className="mobile-landing-page">
                            {/* Quick Stats Badge */}
                            <div className="mobile-stats-badge" onClick={() => setShowMobileAuthModal(true)}>
                                <span className="stats-text">
                                    150+ Recommenders • Join Today
                                </span>
                            </div>

                            {/* Main Heading */}
                            <div className="mobile-heading-container">
                                <h1 className="mobile-main-heading">
                                    What would you like to discover today?
                                </h1>
                                
                                <div className="mobile-subtitle">
                                    <span className="text-muted">Search, </span>
                                    <span className="text-brand">Powered by People You Trust</span>
                                </div>
                            </div>

                            {/* Main Search Area */}
                            <div className="mobile-search-container">
                                <div className="mobile-search-input-group">
                                    <textarea 
                                        ref={searchInputElementRef}
                                        className="mobile-main-search-input" 
                                        placeholder="Ask Anything, @ to search a friend's Recs..." 
                                        value={searchQuery} 
                                        onChange={handleSearchInputChange} 
                                        onKeyDown={handleSearchInputKeyDown}
                                        disabled={isSearching} 
                                        rows="1"
                                    />
                                    
                                    {/* Location Row */}
                                    <div 
                                        className="mobile-location-info"
                                        onClick={() => {
                                            setLocationInput(`${city}, ${state}`);
                                            setIsEditingLocation(true);
                                        }}
                                    >
                                        <MapPinIcon className="mobile-location-icon" />
                                        <span className="mobile-location-text">{city && state ? `${city}, ${state}` : 'Your Location'}</span>
                                    </div>
                                    
                                    {/* Search Button */}
                                    <button 
                                        type="submit" 
                                        className="mobile-search-btn"
                                        disabled={isSearching}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSearch(e);
                                        }}
                                    >
                                        <MagnifyingGlassIcon className="mobile-search-icon" />
                                    </button>
                                </div>
                            </div>

                            {/* Popular Searches */}
                            <div className="mobile-popular-searches">
                                <h3 className="mobile-popular-title">Trending searches in your circle</h3>
                                <div className="mobile-search-suggestions">
                                    <div 
                                        className="mobile-search-suggestion"
                                        onClick={() => {
                                            setSearchQuery("What's @venkat's favorite Thai place in Seattle");
                                            setTimeout(() => {
                                                handleSearch(null, "What's @venkat's favorite Thai place in Seattle");
                                            }, 100);
                                        }}
                                    >
                                        <span className="suggestion-text">"What's @venkat's favorite Thai place in Seattle"</span>
                                        <MagnifyingGlassIcon className="suggestion-icon" />
                                    </div>
                                    <div 
                                        className="mobile-search-suggestion"
                                        onClick={() => {
                                            setSearchQuery("Kati Vegan Thai");
                                            setTimeout(() => {
                                                handleSearch(null, "Kati Vegan Thai");
                                            }, 100);
                                        }}
                                    >
                                        <span className="suggestion-text">"Kati Vegan Thai"</span>
                                        <MagnifyingGlassIcon className="suggestion-icon" />
                                    </div>
                                </div>
                            </div>

                            {/* Not Logged In Public Recs Section */}
                            <div className="mobile-public-recs-section">
                                <h3 className="mobile-public-recs-title">Not Logged In? Here are some Public Recs:</h3>
                            </div>

                            {/* Public Recommendations */}
                            <div className="dashboard-recommendations">
                                <div className="recommendations-feed">
                                    {isLoadingPublicRecommendations && <div className="feed-message">Loading recommendations...</div>}
                                    {publicRecommendationsError && <div className="feed-message feed-error">Could not load recommendations.</div>}
                                    {!isLoadingPublicRecommendations && !publicRecommendationsError && publicRecommendations.length > 0 ? (
                                        <>
                                            {publicRecommendations.map((rec) => (
                                                <RecommendationCard
                                                    key={rec.id}
                                                    rec={rec}
                                                    onWriteReview={handleOpenReviewModal}
                                                    onLike={handleLikeRecommendation}
                                                    isLikedByCurrentUser={false}
                                                    loggedInUserId={null}
                                                    currentUserName={null}
                                                    comments={publicCommentsMap.get(String(rec.provider_id || rec.id)) || []}
                                                    onCommentAdded={handleCommentAdded}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        !isLoadingPublicRecommendations && !publicRecommendationsError && (
                                            <div className="feed-message">
                                                <SignUpButton mode="modal">
                                                    <span className="underline-highlight" style={{ cursor: 'pointer' }}>
                                                        Join to unlock your network's favorites.
                                                    </span>
                                                </SignUpButton>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Mobile Filter Dropdown */}
                            {showMainFilterDropdown && (
                                <div className="mobile-filter-dropdown-overlay" onClick={() => setShowMainFilterDropdown(false)}>
                                    <div className="mobile-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                                        <div className="mobile-filter-dropdown-header">
                                            <h3>Filter Recommendations</h3>
                                            <button 
                                                className="mobile-filter-close-btn"
                                                onClick={() => setShowMainFilterDropdown(false)}
                                            >
                                                <XMarkIcon className="mobile-filter-close-icon" />
                                            </button>
                                        </div>
                                        <div className="mobile-filter-options">
                                            <button 
                                                type="button" 
                                                className="mobile-filter-option selected"
                                                onClick={() => {
                                                    setShowMainFilterDropdown(false);
                                                }}
                                            >
                                                All Recs
                                            </button>
                                            <button 
                                                type="button" 
                                                className="mobile-filter-option"
                                                onClick={() => {
                                                    handleSearch(null);
                                                    setShowMainFilterDropdown(false);
                                                }}
                                            >
                                                My Trust Circle
                                            </button>
                                            <button 
                                                type="button" 
                                                className="mobile-filter-option"
                                                onClick={() => {
                                                    handleSearch(null);
                                                    setShowMainFilterDropdown(false);
                                                }}
                                            >
                                                Specific Trust Circles
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Floating Action Button */}
                            <button 
                                className="mobile-fab" 
                                onClick={() => {
                                    setShowMobileAuthModal(true);
                                }}
                            >
                                <PlusIcon className="mobile-fab-icon" />
                            </button>
                        </div>
                    ) : (
                        // Desktop Layout - Unchanged
                        <>
                            {/* Welcome Message */}
                            <div className="dashboard-welcome">
                                <h1 className="dashboard-title dashboard-title-extended">
                                    Welcome to <span className="highlight-name">Hyve</span>.
                                </h1>
                                <p className="dashboard-subtitle">
                                    The go-to network to discover recommendations from people you <span className="underline-highlight">trust</span>.
                                </p>
                            </div>

                            {/* Search Bar */}
                            <form className="dashboard-search-form dashboard-search-expansive" onSubmit={handleSearch}>
                                {/* Main Search Input */}
                                <div className="search-input-group-expansive">
                                    <div className="search-input-main">
                                        <textarea 
                                            ref={searchInputElementRef}
                                            className="main-search-input-expansive" 
                                            placeholder="Ask anything and @mention a friend to search their Recs..." 
                                            value={searchQuery} 
                                            onChange={handleSearchInputChange} 
                                            onKeyDown={handleSearchInputKeyDown}
                                            disabled={isSearching} 
                                            rows="1"
                                            style={{ resize: 'none', overflow: 'hidden' }}
                                        />
                                        <div className="search-actions">
                                            <button type="submit" className="search-submit-button-expansive" disabled={isSearching}> 
                                                {isSearching ? <span className="search-spinner"></span> : <MagnifyingGlassIcon className="search-button-icon" />} 
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* User Mention Dropdown */}
                                    <UserMentionDropdown
                                        isVisible={showMentionDropdown}
                                        searchTerm={mentionSearchTerm}
                                        onSelectUser={handleMentionSelect}
                                        onClose={handleMentionDropdownClose}
                                        position={mentionDropdownPosition}
                                        currentUserId={user?.id || dbUser?.clerkId}
                                    />
                                    
                                    {/* Hover Tooltip for Existing Mentions */}
                                    {hoveredMention && (
                                        <div 
                                            className="mention-hover-tooltip"
                                            style={{
                                                position: 'fixed',
                                                top: mentionHoverPosition.top,
                                                left: mentionHoverPosition.left,
                                                zIndex: 1001
                                            }}
                                        >
                                            <div className="tooltip-content">
                                                <div className="tooltip-avatar">
                                                    {hoveredMention.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="tooltip-info">
                                                    <div className="tooltip-name">{hoveredMention}</div>
                                                    <div className="tooltip-username">@{hoveredMention}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Location and Trust Circles Row */}
                                    <div className="search-filters-row">
                                        <div className="location-input-wrapper-expansive">
                                            <MapPinIcon className="location-icon" />
                                            {isEditingLocation ? (
                                                <input
                                                    className="location-input-expansive"
                                                    type="text"
                                                    value={locationInput}
                                                    onChange={e => setLocationInput(e.target.value)}
                                                    onBlur={handleUpdateLocation}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateLocation()}
                                                    autoFocus
                                                />
                                            ) : (
                                                <>
                                                    <span className="location-text">{city && state ? `${city}, ${state}` : 'Your Location'}</span>
                                                    <button type="button" className="location-change-button" onClick={() => {
                                                        setLocationInput(`${city}, ${state}`);
                                                        setIsEditingLocation(true);
                                                    }}>
                                                        Change
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        
                                        <div className="search-quick-filters">
                                            {isLoadingTrustCircles ? (
                                                <div className="filter-chip loading-spinner"></div>
                                            ) : trustCircles.length > 0 ? (
                                                <>
                                                    {/* Main filter dropdown */}
                                                    <div className="main-filter-container">
                                                        <button 
                                                            type="button" 
                                                            className={`main-filter-button ${currentFilterMode ? 'active' : ''}`}
                                                            onClick={() => setShowMainFilterDropdown(!showMainFilterDropdown)}
                                                        >
                                                            <span>{getFilterModeDisplayText()}</span>
                                                            <span className="dropdown-arrow">▼</span>
                                                        </button>
                                                        
                                                        {showMainFilterDropdown && (
                                                            <div className="main-filter-dropdown">
                                                                <button 
                                                                    type="button" 
                                                                    className={`dropdown-item ${currentFilterMode === 'all' ? 'selected' : ''}`}
                                                                    onClick={() => handleFilterModeChange('all')}
                                                                >
                                                                    All Recs
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    className={`dropdown-item ${currentFilterMode === 'myTrust' ? 'selected' : ''}`}
                                                                    onClick={() => handleFilterModeChange('myTrust')}
                                                                >
                                                                    My Trust Circle
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    className={`dropdown-item specific-trust-circles ${currentFilterMode === 'specific' ? 'selected' : ''}`}
                                                                    onClick={handleSpecificTrustCirclesClick}
                                                                    onMouseEnter={handleSpecificTrustCirclesHover}
                                                                    onMouseLeave={handleSpecificTrustCirclesLeave}
                                                                >
                                                                    Specific Trust Circles
                                                                    <span className="dropdown-arrow">▶</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Selected specific trust circles */}
                                                    {selectedTrustCircles.length > 0 && (
                                                        selectedTrustCircles.map((circle) => (
                                                            <button 
                                                                key={circle.id} 
                                                                type="button" 
                                                                className="filter-chip selected" 
                                                                onClick={() => handleTrustCircleSelection(circle.id, circle.name)}
                                                            >
                                                                {circle.name}
                                                                <span className="remove-chip">×</span>
                                                            </button>
                                                        ))
                                                    )}
                                                    
                                                    {/* Dropdown for specific trust circles */}
                                                    {showTrustCircleDropdown && (
                                                        <div 
                                                            className="trust-circle-dropdown trust-circle-hover-dropdown"
                                                            onMouseEnter={handleTrustCircleDropdownHover}
                                                            onMouseLeave={handleTrustCircleDropdownLeave}
                                                        >
                                                            <div className="dropdown-header">
                                                                Select Trust Circles:
                                                            </div>
                                                            {trustCircles.map((circle) => {
                                                                const isSelected = selectedTrustCircles.find(selected => selected.id === circle.id);
                                                                return (
                                                                    <button 
                                                                        key={circle.id} 
                                                                        type="button" 
                                                                        className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                                                                        onClick={() => handleTrustCircleSelection(circle.id, circle.name)}
                                                                    >
                                                                        {circle.name}
                                                                        {isSelected && <span className="checkmark">✓</span>}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <button 
                                                    type="button" 
                                                    className="filter-chip build-trust-circle"
                                                    onClick={() => navigate('/trustcircles')}
                                                >
                                                    Build Your Trust Circle
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* AI Suggestions */}
                                <div className="search-suggestions">
                                    <div className="suggestion-chips">
                                        <span className="suggestion-label">Trending Searches:</span>
                                        <button 
                                            type="button"
                                            className="suggestion-chip"
                                            onClick={() => {
                                                setSearchQuery("What's @Venkat's favorite Thai place in Seattle");
                                                setTimeout(() => {
                                                    const form = document.querySelector('.dashboard-search-form');
                                                    if (form) {
                                                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                                    }
                                                }, 100);
                                            }}
                                        >
                                            "What's @Venkat's favorite Thai place in Seattle"
                                        </button>
                                        <button 
                                            type="button"
                                            className="suggestion-chip"
                                            onClick={() => {
                                                setSearchQuery("Kati Vegan Thai");
                                                setTimeout(() => {
                                                    const form = document.querySelector('.dashboard-search-form');
                                                    if (form) {
                                                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                                    }
                                                }, 100);
                                            }}
                                        >
                                            "Kati Vegan Thai"
                                        </button>
                                    </div>
                                </div>
                            </form>

                            {/* Public Recommendations */}
                            <div className="dashboard-recommendations">
                                <h2 className="section-title">
                                    Not logged in? Here are some recent public Recs:
                                </h2>
                                <div className="recommendations-feed">
                                    {isLoadingPublicRecommendations && <div className="feed-message">Loading recommendations...</div>}
                                    {publicRecommendationsError && <div className="feed-message feed-error">Could not load recommendations.</div>}
                                    {!isLoadingPublicRecommendations && !publicRecommendationsError && publicRecommendations.length > 0 ? (
                                        <>
                                            {publicRecommendations.map((rec) => (
                                                <RecommendationCard
                                                    key={rec.id}
                                                    rec={rec}
                                                    onWriteReview={handleOpenReviewModal}
                                                    onLike={handleLikeRecommendation}
                                                    isLikedByCurrentUser={false}
                                                    loggedInUserId={null}
                                                    currentUserName={null}
                                                    comments={publicCommentsMap.get(String(rec.provider_id || rec.id)) || []}
                                                    onCommentAdded={handleCommentAdded}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        !isLoadingPublicRecommendations && !publicRecommendationsError && (
                                            <div className="feed-message">
                                                <SignUpButton mode="modal">
                                                    <span className="underline-highlight" style={{ cursor: 'pointer' }}>
                                                        Join to unlock your network's favorites.
                                                    </span>
                                                </SignUpButton>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Right Column - Hidden on Mobile for non-logged-in */}
                <div className="dashboard-right desktop-only-right">
                    {/* Social Proof Banner */}
                    <div className="social-proof-banner">
                        <div className="social-proof-content">
                            <div className="social-proof-number">150+</div>
                            <div className="social-proof-text">trusted recommenders</div>
                            <div className="social-proof-subtext">building their Trust Circle</div>
                        </div>
                        <div className="social-proof-gradient"></div>
                    </div>

                    {/* Live Activity Feed */}
                    <div className="activity-feed-container">
                        <h3 className="section-title">Live Activity</h3>
                        <div className="activity-feed">
                            {randomActivities.map((activity, index) => (
                                <div key={index} className="activity-item">
                                    <div className="activity-avatar">{activity.avatar}</div>
                                    <div className="activity-content">
                                        <div className="activity-text" dangerouslySetInnerHTML={{ __html: activity.text }}></div>
                                        <div className="activity-time">{activity.time}</div>
                                    </div>
                                    {activity.hasPulse && <div className="activity-pulse"></div>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Community Highlights */}
                    <div className="community-highlights-container">
                        <h3 className="section-title">Most Active This Week</h3>
                        <div className="community-highlights">
                            <div className="community-highlight-item">
                                <div className="community-icon">🏠</div>
                                <div className="community-info">
                                    <div className="community-name">Home Services</div>
                                    <div className="community-activity">234 new recommendations</div>
                                </div>
                            </div>
                            <div className="community-highlight-item">
                                <div className="community-icon">🍽️</div>
                                <div className="community-info">
                                    <div className="community-name">Restaurants</div>
                                    <div className="community-activity">189 new recommendations</div>
                                </div>
                            </div>
                            <div className="community-highlight-item">
                                <div className="community-icon">💰</div>
                                <div className="community-info">
                                    <div className="community-name">Financial Services</div>
                                    <div className="community-activity">156 new recommendations</div>
                                </div>
                            </div>
                        </div>
                        <SignUpButton mode="modal">
                            <button className="cta-join-button">
                                Join the Action
                            </button>
                        </SignUpButton>
                    </div>
                </div>
            </div>

            <ReviewModal
                isOpen={isReviewModalOpen}
                onClose={handleCloseReviewModal}
                onSubmit={handleSubmitReview}
                provider={providerForReview}
            />

            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                message={successMessage}
            />

            <NotificationModal
                isOpen={isNotificationModalOpen}
                onClose={handleCloseNotificationModal}
                notifications={notifications}
                isLoading={isLoadingNotifications}
                onMarkAsRead={handleMarkNotificationAsRead}
                onMarkAllAsRead={handleMarkAllNotificationsAsRead}
            />

            {showLocationModal && (
                <div className="location-modal-overlay">
                    <div className="location-modal-content">
                        <button className="location-modal-close" onClick={() => setShowLocationModal(false)}> <XMarkIcon /> </button>
                        <h3>Expanding Our Horizons!</h3>
                        <p>We're currently focused on serving the <strong>Greater Seattle Area</strong>.</p>
                        <p>We're working hard to expand and will be launching nationally soon. Stay tuned!</p>
                    </div>
                </div>
            )}

            {showMobileAuthModal && (
                <div className="mobile-auth-modal-overlay">
                    <div className="mobile-auth-modal">
                        <button className="mobile-auth-close" onClick={() => setShowMobileAuthModal(false)}> <XMarkIcon /> </button>
                        <h3>Join to Search</h3>
                        <p>Sign up or sign in to search recommendations from people you trust.</p>
                        <div className="mobile-auth-buttons">
                            <SignUpButton mode="modal">
                                <button className="button-primary">Sign Up</button>
                            </SignUpButton>
                            <SignInButton mode="modal">
                                <button className="button-secondary">Already have an account? Sign In</button>
                            </SignInButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;